/**
 * NOVA CHARTER — balance table and shared constants.
 *
 * Everything tunable lives here so the balance harness (`scripts/balance.mts`) and the
 * tests can reason about the game without reading the engine.
 */

import type {
  ActionKind, MegastructureKind, PlayerId, RelicKind, Region, Resources,
  SystemClass, TechTrack,
} from './types';

/* ------------------------------------------------------------------ identity */

export const GAME_TITLE = 'NOVA CHARTER';
export const GAME_TAGLINE = 'Four charters. One galaxy.';
export const GAME_VERSION = '1.0.0';

/* --------------------------------------------------------------------- board */

/** Galaxy radius in hexes. Radius 4 → 61 systems. */
export const MAP_RADIUS = 4;

export const RESOURCE_KINDS = ['ore', 'fuel', 'biomass', 'data'] as const;

export const ZERO_RESOURCES: Readonly<Resources> = Object.freeze({
  ore: 0, fuel: 0, biomass: 0, data: 0,
});

/** Per-resource storage cap. Excess is trimmed at Upkeep. */
export const RESOURCE_CAP = 15;

export const MAX_FLEETS = 5;
export const MAX_POPULATION = 12;

/** Base Command Points per round, before population and tech bonuses. */
export const BASE_CP = 5;
/** One extra CP per this many population. */
export const CP_PER_POPULATION = 4;

/** Population eats biomass on even-numbered rounds. */
export const UPKEEP_ROUNDS_MODULO = 2;

export const STARTING_RESOURCES: Readonly<Resources> = Object.freeze({
  ore: 4, fuel: 4, biomass: 3, data: 2,
});

/**
 * Homeworlds override their class yield with a balanced capital output.
 *
 * This is load-bearing, not flavour. Homeworlds are Terran, and Terran yields Biomass
 * only — so without an override a player has *zero* Ore income, while claiming and
 * upgrading both cost Ore. Self-play deadlocked at under one colony per match until the
 * capital produced a little of everything.
 */
export const HOMEWORLD_YIELD: Readonly<Resources> = Object.freeze({
  ore: 1, fuel: 1, biomass: 1, data: 1,
});
export const STARTING_POPULATION = 2;
export const STARTING_FLEETS = 2;

export const DEFAULT_TOTAL_ROUNDS = 8;
/** Advisory clock: 8 rounds × 7 min ≈ 56 min plus setup and scoring. */
export const DEFAULT_ROUND_SECONDS = 420;

/* ------------------------------------------------------------- system classes */

export interface SystemClassDef {
  readonly cls: SystemClass;
  readonly name: string;
  readonly yield: Resources;
  readonly habitability: number;
  readonly hazard: number;
  /** Relative draw weight during map generation. */
  readonly weight: number;
  /** Base disc colour for the renderer. */
  readonly color: string;
  /** Secondary colour for bands / atmosphere. */
  readonly accent: string;
}

export const SYSTEM_CLASSES: Readonly<Record<SystemClass, SystemClassDef>> = Object.freeze({
  terran: {
    cls: 'terran', name: 'Terran', weight: 3,
    yield: { ore: 0, fuel: 0, biomass: 2, data: 0 },
    habitability: 3, hazard: 0, color: '#3f9d6b', accent: '#8fe3b0',
  },
  oceanic: {
    cls: 'oceanic', name: 'Oceanic', weight: 5,
    yield: { ore: 0, fuel: 0, biomass: 1, data: 1 },
    habitability: 3, hazard: 1, color: '#2f7fb8', accent: '#7fd4ff',
  },
  ice: {
    cls: 'ice', name: 'Ice', weight: 8,
    yield: { ore: 0, fuel: 1, biomass: 1, data: 0 },
    habitability: 2, hazard: 1, color: '#8fb6cc', accent: '#e3f6ff',
  },
  desert: {
    cls: 'desert', name: 'Desert', weight: 8,
    yield: { ore: 1, fuel: 0, biomass: 1, data: 0 },
    habitability: 2, hazard: 1, color: '#c99a54', accent: '#f2d9a8',
  },
  volcanic: {
    cls: 'volcanic', name: 'Volcanic', weight: 8,
    yield: { ore: 2, fuel: 0, biomass: 0, data: 0 },
    habitability: 1, hazard: 2, color: '#a8452f', accent: '#ff9a5e',
  },
  gasGiant: {
    cls: 'gasGiant', name: 'Gas Giant', weight: 8,
    yield: { ore: 0, fuel: 2, biomass: 0, data: 0 },
    habitability: 1, hazard: 1, color: '#b07ac4', accent: '#e8bcf5',
  },
  asteroid: {
    cls: 'asteroid', name: 'Asteroid Belt', weight: 7,
    yield: { ore: 1, fuel: 1, biomass: 0, data: 0 },
    habitability: 1, hazard: 2, color: '#7d7a86', accent: '#c2bfcc',
  },
  anomaly: {
    cls: 'anomaly', name: 'Anomaly', weight: 4,
    yield: { ore: 0, fuel: 0, biomass: 0, data: 2 },
    habitability: 1, hazard: 3, color: '#4d3f9e', accent: '#b4a6ff',
  },
  deepSpace: {
    cls: 'deepSpace', name: 'Deep Space', weight: 6,
    yield: { ore: 0, fuel: 0, biomass: 0, data: 0 },
    habitability: 0, hazard: 0, color: '#1b1c2a', accent: '#2b2d40',
  },
});

/** Classes eligible for random placement (deep space is placed separately). */
export const COLONISABLE_CLASSES: readonly SystemClass[] = [
  'terran', 'oceanic', 'ice', 'desert', 'volcanic', 'gasGiant', 'asteroid', 'anomaly',
];

/* ------------------------------------------------------------------- regions */

export interface RegionDef {
  readonly region: Region;
  readonly name: string;
  /** Inclusive ring range. */
  readonly rings: readonly [number, number];
  /** VP for the majority holder at a region scoring. */
  readonly majorityVp: number;
  /** VP for the runner-up (0 if none). */
  readonly runnerUpVp: number;
  readonly tint: string;
}

export const REGIONS: Readonly<Record<Region, RegionDef>> = Object.freeze({
  core: {
    region: 'core', name: 'The Core', rings: [0, 1],
    majorityVp: 5, runnerUpVp: 2, tint: '#ff8a4c',
  },
  midRim: {
    region: 'midRim', name: 'Mid Rim', rings: [2, 3],
    majorityVp: 3, runnerUpVp: 1, tint: '#4cc4ff',
  },
  outerRim: {
    region: 'outerRim', name: 'Outer Rim', rings: [4, 4],
    majorityVp: 2, runnerUpVp: 0, tint: '#8f7cff',
  },
});

/** Rounds on which region majorities are scored. */
export const REGION_SCORING_ROUNDS: readonly number[] = [4, 8];

export function regionForRing(ring: number): Region {
  if (ring <= 1) return 'core';
  if (ring <= 3) return 'midRim';
  return 'outerRim';
}

/* ------------------------------------------------------------------- actions */

export interface ActionDef {
  readonly kind: ActionKind;
  readonly name: string;
  readonly cp: number;
  readonly cost: Readonly<Resources>;
  readonly blurb: string;
  /** Short glyph used on the radial menu. */
  readonly glyph: string;
  readonly hotkey: string;
}

export const ACTIONS: Readonly<Record<ActionKind, ActionDef>> = Object.freeze({
  survey: {
    kind: 'survey', name: 'Survey', cp: 1, cost: ZERO_RESOURCES, glyph: '◎', hotkey: '1',
    blurb: 'Reveal an unsurveyed system next to your fleets or colonies. Gain 1 Data.',
  },
  move: {
    kind: 'move', name: 'Move Fleet', cp: 1, cost: ZERO_RESOURCES, glyph: '➤', hotkey: '2',
    blurb: 'Jump a fleet. First jump is free, each further jump costs 1 Fuel.',
  },
  deployFleet: {
    kind: 'deployFleet', name: 'Deploy Fleet', cp: 2, glyph: '⬟', hotkey: '3',
    cost: { ore: 2, fuel: 0, biomass: 0, data: 0 },
    blurb: 'Build a new fleet at one of your colonies.',
  },
  claim: {
    kind: 'claim', name: 'Claim', cp: 2, glyph: '⬢', hotkey: '4',
    cost: { ore: 2, fuel: 0, biomass: 1, data: 0 },
    blurb: 'Found an Outpost where you have a fleet. Hazard adds Ore to the cost.',
  },
  upgrade: {
    kind: 'upgrade', name: 'Upgrade', cp: 2, glyph: '⬢⁺', hotkey: '5',
    cost: { ore: 3, fuel: 0, biomass: 2, data: 0 },
    blurb: 'Grow an Outpost into a Colony. Doubles yield, +1 Population.',
  },
  terraform: {
    kind: 'terraform', name: 'Terraform', cp: 3, glyph: '❋', hotkey: '6',
    cost: { ore: 0, fuel: 2, biomass: 2, data: 0 },
    blurb: '+1 Habitability and −1 Hazard on a system you own.',
  },
  research: {
    kind: 'research', name: 'Research', cp: 2, cost: ZERO_RESOURCES, glyph: '⚛', hotkey: '7',
    blurb: 'Buy a tech from the market with Data. Tiers unlock in order.',
  },
  trade: {
    kind: 'trade', name: 'Trade', cp: 1, cost: ZERO_RESOURCES, glyph: '⇄', hotkey: '8',
    blurb: 'Give 3 of one resource, take 2 of another.',
  },
  megastructure: {
    kind: 'megastructure', name: 'Megastructure', cp: 4, glyph: '✺', hotkey: '9',
    cost: { ore: 8, fuel: 4, biomass: 0, data: 4 },
    blurb: 'Once per game. 8 VP and a permanent empire-wide power.',
  },
  pass: {
    kind: 'pass', name: 'Pass', cp: 0, cost: ZERO_RESOURCES, glyph: '⏻', hotkey: '0',
    blurb: 'Leave this round\'s Command phase. Gain 1 Fuel.',
  },
});

/** Hazard surcharge, in Ore, per hazard point when claiming. */
export const HAZARD_CLAIM_ORE = 1;
/** Trade ratio: give this many, take TRADE_TAKE. */
export const TRADE_GIVE = 3;
export const TRADE_TAKE = 2;
/** Fuel granted for passing. */
export const PASS_FUEL_BONUS = 1;
/** Base jump range for a fleet, before Drive tech. */
export const BASE_JUMP_RANGE = 2;
/** Jumps that cost no fuel each move action. */
export const FREE_JUMPS = 1;

/* ------------------------------------------------------------------ contests */

/** Strength contributed by each fleet present. */
export const CONTEST_FLEET_STRENGTH = 2;
/** Extra strength for the system's owner. */
export const CONTEST_DEFENDER_BONUS = 1;
/** Fleets destroyed for the losing side. */
export const CONTEST_LOSER_FLEET_LOSS = 1;

/* ---------------------------------------------------------------- scoring VP */

export const VP = Object.freeze({
  outpost: 1,
  colony: 3,
  /** Colonies on habitability-3 worlds are worth this instead. */
  colonyPrime: 4,
  relic: 2,
  megastructure: 8,
  /** 1 VP per this many population. */
  populationPer: 3,
});

/* -------------------------------------------------------------------- relics */

export interface RelicDef {
  readonly kind: RelicKind;
  readonly name: string;
  readonly blurb: string;
  /** One-shot resource payout on claim. */
  readonly payout: Readonly<Resources>;
}

export const RELICS: Readonly<Record<RelicKind, RelicDef>> = Object.freeze({
  cache: {
    kind: 'cache', name: 'Supply Cache', blurb: 'Sealed pre-collapse stores.',
    payout: { ore: 4, fuel: 2, biomass: 0, data: 0 },
  },
  archive: {
    kind: 'archive', name: 'Data Archive', blurb: 'A library that outlived its library.',
    payout: { ore: 0, fuel: 0, biomass: 0, data: 4 },
  },
  foundry: {
    kind: 'foundry', name: 'Void Foundry', blurb: 'Still warm, somehow.',
    payout: { ore: 5, fuel: 0, biomass: 0, data: 1 },
  },
  seedVault: {
    kind: 'seedVault', name: 'Seed Vault', blurb: 'Ten thousand futures, frozen.',
    payout: { ore: 0, fuel: 0, biomass: 4, data: 1 },
  },
  beacon: {
    kind: 'beacon', name: 'Jump Beacon', blurb: 'It is still calling something.',
    payout: { ore: 0, fuel: 5, biomass: 0, data: 1 },
  },
});

/** How many relics are seeded into the galaxy. */
export const RELIC_COUNT = 8;

/* ----------------------------------------------------------- megastructures */

export interface MegastructureDef {
  readonly kind: MegastructureKind;
  readonly name: string;
  readonly blurb: string;
}

export const MEGASTRUCTURES: Readonly<Record<MegastructureKind, MegastructureDef>> =
  Object.freeze({
    ringworld: {
      kind: 'ringworld', name: 'Ringworld',
      blurb: '+2 Biomass each round and +1 maximum Population.',
    },
    dysonSwarm: {
      kind: 'dysonSwarm', name: 'Dyson Swarm',
      blurb: '+3 Fuel each round.',
    },
    gateNexus: {
      kind: 'gateNexus', name: 'Gate Nexus',
      blurb: '+2 jump range for all your fleets, and +1 Command Point.',
    },
    arkFoundry: {
      kind: 'arkFoundry', name: 'Ark Foundry',
      blurb: '+2 Ore each round and Deploy Fleet costs no Ore.',
    },
  });

/* ---------------------------------------------------------------- tech table */

export const TECH_TRACK_NAMES: Readonly<Record<TechTrack, string>> = Object.freeze({
  drive: 'Drive',
  industry: 'Industry',
  bio: 'Biosphere',
  science: 'Science',
});

export const TECH_MARKET_SIZE = 4;

/* ----------------------------------------------------------------- presentation */

/** Seat colours. Chosen for deuteranopia separation and 3-metre legibility. */
export const PLAYER_COLORS: readonly string[] = [
  '#ff7a4c', // 0 — ember orange
  '#4cc9ff', // 1 — ion cyan
  '#b5f24c', // 2 — chlorophyll
  '#ff5fd2', // 3 — magenta flare
];

export const PLAYER_NAMES_DEFAULT: readonly string[] = [
  'Ember Charter', 'Ion Charter', 'Verdant Charter', 'Flare Charter',
];

/** Short seat labels for the HUD. */
export const PLAYER_SHORT: readonly string[] = ['P1', 'P2', 'P3', 'P4'];

export const ALL_SEATS: readonly PlayerId[] = [0, 1, 2, 3];

/** Hex render geometry, in world units before camera zoom. */
export const HEX_SIZE = 56;
/** Gap between hex edges, world units. */
export const HEX_GAP = 3;

/** Deadzone for analog sticks. */
export const STICK_DEADZONE = 0.35;
/** Cursor auto-repeat: first delay, then interval (ms). */
export const REPEAT_DELAY_MS = 180;
export const REPEAT_INTERVAL_MS = 90;
/** A trigger counts as pressed above this. */
export const TRIGGER_THRESHOLD = 0.5;
